import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './button';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-amber-500/20">
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/phos-logo.webp" alt="PHOS Studio" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-black text-white tracking-tight">PHOS STUDIO</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-slate-300 hover:text-amber-400 font-medium transition-colors">Home</Link>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-slate-300 hover:text-amber-400 font-medium transition-colors"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
                  <Link to="/editing" onClick={() => setServicesOpen(false)} className="block px-5 py-3 text-slate-300 hover:text-amber-400 hover:bg-amber-500/10 font-medium transition-colors border-b border-slate-800">
                    Video Editing
                  </Link>
                  <Link to="/social-media-service" onClick={() => setServicesOpen(false)} className="block px-5 py-3 text-slate-300 hover:text-amber-400 hover:bg-amber-500/10 font-medium transition-colors border-b border-slate-800">
                    Social Media
                  </Link>
                  <Link to="/ugc-production" onClick={() => setServicesOpen(false)} className="block px-5 py-3 text-slate-300 hover:text-amber-400 hover:bg-amber-500/10 font-medium transition-colors">
                    UGC Production
                  </Link>
                </div>
              )}
            </div>

            <Link to="/portfolio" className="text-slate-300 hover:text-amber-400 font-medium transition-colors">Portfolio</Link>
            <Link to="/contact" className="text-slate-300 hover:text-amber-400 font-medium transition-colors">Contact</Link>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden sm:block">
              <Button className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 transition-all hover:scale-105">
                Get Started
              </Button>
            </Link>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-slate-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-800 pt-4 animate-fade-in">
            <div className="flex flex-col gap-2">
              <Link to="/" onClick={() => setMobileOpen(false)} className="text-slate-300 hover:text-amber-400 font-medium transition-colors py-2">Home</Link>

              {/* Mobile Services Accordion */}
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center justify-between text-slate-300 hover:text-amber-400 font-medium transition-colors py-2"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 flex flex-col gap-1 border-l-2 border-amber-500/30 ml-2">
                  <Link to="/editing" onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-amber-400 font-medium transition-colors py-2">Video Editing</Link>
                  <Link to="/social-media-service" onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-amber-400 font-medium transition-colors py-2">Social Media</Link>
                  <Link to="/ugc-production" onClick={() => setMobileOpen(false)} className="text-slate-400 hover:text-amber-400 font-medium transition-colors py-2">UGC Production</Link>
                </div>
              )}

              <Link to="/portfolio" onClick={() => setMobileOpen(false)} className="text-slate-300 hover:text-amber-400 font-medium transition-colors py-2">Portfolio</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="text-slate-300 hover:text-amber-400 font-medium transition-colors py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="sm:hidden mt-2">
                <Button className="w-full bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold shadow-lg shadow-amber-500/30">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
